import {useEffect, useRef } from "react";
import "../styles/DrawingCanvas.css";
import * as fabric from "fabric";

import paintbrushCursor from "../static-img/paint-brush-icon.png";

function DrawingCanvas({ selectedColor, onCanvasReady }) {
  const canvasRef = useRef(null);        // Ref to <canvas> element
  const fabricCanvasRef = useRef(null);  // Ref to Fabric.js instance
  const selectedColorRef = useRef(selectedColor);

  // set up fabric.js canvas, set up drawings
  useEffect(() => {

    const canvasElement = canvasRef.current;
    const getCanvasWidth = () => {
      // Use the actual wrapper width to avoid 1px-ish clipping
      // caused by `100vw` vs scrollbar width differences.
      return canvasElement?.parentElement?.clientWidth || window.innerWidth;
    };

    const getMainContentHeight = () => {

      const mainContent = document.querySelector(".main-content");
      const footer = document.querySelector(".footer");
      const mainHeight = mainContent ? mainContent.offsetHeight : 0;
      const footerHeight = footer ? footer.offsetHeight : 0;

      return mainHeight + footerHeight;
     
    };

    // Initial height based on main-content
    const fabricCanvas = new fabric.Canvas(canvasElement, {
      height: getMainContentHeight(),
      width: getCanvasWidth(),
      isDrawingMode: true,
    });

    fabricCanvasRef.current = fabricCanvas;
    if (onCanvasReady) onCanvasReady(fabricCanvas);
    

    fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(fabricCanvas);
    fabricCanvas.freeDrawingBrush.color = selectedColorRef.current;
    fabricCanvas.freeDrawingBrush.width = 4;


    // Resize handler
    const updateCanvasSize = () => {
      const newHeight = getMainContentHeight();
      fabricCanvas.setHeight(newHeight);
      fabricCanvas.setWidth(getCanvasWidth());
      fabricCanvas.renderAll(); // important to refresh canvas after resize
    };

    // Match the global cursor image used in `App.css`
    // Hotspot coordinates (x y) must match the "tip" of the cursor image.
    fabricCanvas.freeDrawingCursor = `url("${paintbrushCursor}") 25 45, auto`;


    // Attach resize listener
    window.addEventListener("resize", updateCanvasSize);  

    return () => {
      fabricCanvas.dispose();
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, [onCanvasReady]);

  // Update brush color when it changes
  useEffect(() => {
    if (fabricCanvasRef.current) {
      selectedColorRef.current = selectedColor;
      fabricCanvasRef.current.isDrawingMode = false;
      fabricCanvasRef.current.freeDrawingBrush = new fabric.PencilBrush(fabricCanvasRef.current);
      fabricCanvasRef.current.freeDrawingBrush.color = selectedColor;
      fabricCanvasRef.current.freeDrawingBrush.width = 4;
      fabricCanvasRef.current.isDrawingMode = true;
    }
  }, [selectedColor]);

  //Load static images and keep on
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
  
    const fabricCanvas = fabricCanvasRef.current;
  
    fabricCanvas.clear();



    const combinedJSON = {
      objects: [],
      background: null,
      backgroundImage: null,
      overlay: null,
      overlayImage: null,
      clipPath: null,
    };
    
  
    fabricCanvas.loadFromJSON(combinedJSON)
      .then(canvas => {
        canvas.requestRenderAll();
        console.log("Loaded drawing successfully!");
      })
      .catch(err => {
        console.error("Failed to load drawing:", err);
      });
  }, []);

  // Helpers you can call later to export the canvas.
  // Intentionally not wired to any UI/event so normal drawing behavior stays unchanged.
  // eslint-disable-next-line no-unused-vars
  const saveDrawingAsPNG = (filename = "drawing.png") => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL({
      format: "png",
      quality: 1.0,
    });

    const link = document.createElement("a");
    link.href = dataURL;
    link.download = filename;
    link.click();
  };

  // eslint-disable-next-line no-unused-vars
  const saveDrawingAsJSON = (filename = "drawing.json") => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const json = canvas.toJSON();
    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="Canvas">
      <canvas ref={canvasRef} />
    </div>
  );
}

export default DrawingCanvas;
